import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import MatterTransaction from './matterTransaction';
import { useFetchDashboardTransactions } from '../../../hooks/useFetchTransactions';
import { useUser } from '../../../context/UserContext';
import { usePage } from '../../../context/PageContext';
import AddEditTransactionHeader from './addEditTransactionHeader';
import { useTransaction } from '../../../context/TransactionContext';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../../hooks/useEscapeToGoBack';
import { ITransaction } from '../../../interface';
import CustomTabs from '../../customTabs/customTabs';
import CustomModal from '../../informationPopup/customModal';
import { Slash } from '../../icons';
import { useDiscardDialog } from '../../../hooks/useDiscardDialog';
import { isTransactionFormChanged } from './isTransactionFormChanged';

const SubjectSuggestion = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { setTitle } = usePage();
  const { transaction, updateTransaction, initialTransaction } = useTransaction();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const { showDiscardDialog, promptDiscardIfChanged, handleDiscardConfirm, handleDiscardCancel } =
    useDiscardDialog();
  const [selectedTab, setSelectedTab] = useState<string>('0');
  const [searchValue, setSearchValue] = useState<string>('');
  const {
    data: transactionList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    refetch,
  } = useFetchDashboardTransactions({
    userId: user?.id,
    sortBy: ['transactionDate:DESC'],
    pageSize: 50,
    search: searchValue,
    extraFilters: {
      ...(selectedTab === '1' && user?.id ? { transactionUserId: user?.id } : {}),
      ...(selectedTab === '0' && transaction?.matter?.id
        ? { matterId: transaction?.matter?.id }
        : {}),
    },
    enabled: !(Number.parseInt(selectedTab, 10) === 0 && !transaction?.matter?.id),
  });
  const handleRefreshTransactionList = () => {
    return refetch();
  };
  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleClose = () => {
    promptDiscardIfChanged(
      isTransactionFormChanged(transaction, initialTransaction, !!transaction?.id),
      () => {
        updateTransaction(null);
        goBack(undefined, 2);
      },
    );
  };
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={t('common:addSubject')}
        showCross
      />,
    );
  }, []);
  const handleSubjectSelect = (value: string) => {
    if (value) {
      updateTransaction({
        ...transaction,
        currentSubject: value,
      } as ITransaction);
      goBackWithSelection();
    }
  };

  const items = useMemo(
    () => [
      {
        key: '0',
        label: t('common:tab.matterTransactions'),
        children: (
          <MatterTransaction
            data-testid="matter-transaction"
            transactions={transactionList}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isLoading}
            isFetching={isFetching}
            onSelect={handleSubjectSelect}
            searchValue={searchValue}
            onSearch={setSearchValue}
            onRefresh={handleRefreshTransactionList}
          />
        ),
      },
      {
        key: '1',
        label: t('common:tab.ownTransactions'),
        children: (
          <MatterTransaction
            data-testid="own-transaction"
            transactions={transactionList}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isLoading}
            isFetching={isFetching}
            onSelect={handleSubjectSelect}
            searchValue={searchValue}
            onSearch={setSearchValue}
            onRefresh={handleRefreshTransactionList}
          />
        ),
      },
    ],
    [
      transactionList,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      isLoading,
      isFetching,
      searchValue,
      handleRefreshTransactionList,
    ],
  );

  return (
    <div className="dashboard-wrapper">
      <CustomTabs
        data-testid="subject-suggestion-tabs"
        activeKey={selectedTab}
        onChange={setSelectedTab}
        items={items}
      />
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </div>
  );
};

export default SubjectSuggestion;
